import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { format } from 'date-fns';
import type { ColDef } from '../util/DataTable';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import CalendarPicker from '../util/CalendarPicker';

interface Contract {
  id: number;
  title: string;
  description?: string;
  status: 'draft' | 'pending' | 'signed';
  deadline?: string;
  original_name?: string;
  file_url?: string;
  file_size?: number;
  signature_url?: string;
  signedAt?: string;
  createdAt: string;
  employee?: { id: number; first_name: string; last_name: string };
  uploadedBy?: { id: number; first_name: string; last_name: string };
  signedBy?: { id: number; first_name: string; last_name: string };
}

interface Props {
  access: any;
  baseUrl: string;
}

const STATUS_COLORS: Record<string, string> = {
  signed: 'var(--color-success, #059669)',
  pending: '#d97706',
  draft:   'var(--text-color-calendar-week)',
};

const statusLabel = (s: string) => {
  if (s === 'signed')  return t('ContractSigned');
  if (s === 'pending') return t('ContractPending');
  return t('ContractDraft');
};

const fmtDate = (d?: string) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; }
};

const fmtSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Signature canvas ────────────────────────────────────
const SignatureCanvas: React.FC<{ onSign: (base64: string) => void; onClear: () => void; hasSignature: boolean }> = ({ onSign, onClear, hasSignature }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  const getPos = (e: MouseEvent | TouchEvent, rect: DOMRect) => {
    if ('touches' in e) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const start = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      drawing.current = true;
      const rect = canvas.getBoundingClientRect();
      const p = getPos(e, rect);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!drawing.current) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const p = getPos(e, rect);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      onSign(canvas.toDataURL('image/png'));
    };
    const end = () => { drawing.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []); // eslint-disable-line

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    onClear();
  };

  return (
    <div>
      <div style={{
        border: '1.5px dashed var(--border-input)',
        borderRadius: 6,
        background: 'var(--bg-input)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {!hasSignature && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', gap: 4,
          }}>
            <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)' }}>{t('SignHere')}</div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', opacity: .7 }}>{t('SignHereSub')}</div>
          </div>
        )}
        <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 130, cursor: 'crosshair', touchAction: 'none' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 }}>
        <button
          type="button"
          onClick={clear}
          style={{
            background: 'none', border: 'none', fontSize: 12,
            color: 'var(--text-color-calendar-week)', cursor: 'pointer', padding: '3px 6px',
          }}>
          {t('Clear')}
        </button>
        {hasSignature && (
          <span style={{ fontSize: 11, color: STATUS_COLORS.signed, fontWeight: 600 }}>
            ✓ {t('SignatureCaptured')}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Contract viewer (detail view) ──────────────────────
const ContractViewer: React.FC<{
  contract: Contract;
  baseUrl: string;
  canManage: boolean;
  onBack: () => void;
  onSigned: (c: Contract) => void;
}> = ({ contract, baseUrl, canManage, onBack, onSigned }) => {
  const [sigBase64, setSigBase64] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedState, setSignedState] = useState(contract.status === 'signed');

  const handleSign = async () => {
    if (!hasSignature || signing) return;
    setSigning(true);
    try {
      const { data } = await axios.post(`document-contract/${contract.id}/sign`, { signatureBase64: sigBase64 });
      setSignedState(true);
      onSigned(data);
    } catch { /* handled by global interceptor */ }
    finally { setSigning(false); }
  };

  const fileUrl = contract.file_url ? `${baseUrl}${contract.file_url.replace(/^\.\//, '')}` : null;

  return (
    <div>
      {/* Back + title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
            borderRadius: 5, padding: '5px 12px', fontSize: 12,
            color: 'var(--text-color)', cursor: 'pointer',
          }}>
          ← {t('Back')}
        </button>
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-color)', flex: 1 }}>
          {contract.title}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
          background: `${STATUS_COLORS[contract.status]}22`,
          color: STATUS_COLORS[contract.status],
          border: `1px solid ${STATUS_COLORS[contract.status]}44`,
        }}>
          {statusLabel(contract.status)}
        </span>
      </div>

      {/* 2-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 12, alignItems: 'start' }}>

        {/* Left: document preview */}
        <div style={{
          background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
          borderRadius: 6, overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--bg-top)', borderBottom: '1px solid var(--border-input)',
            padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>
                {contract.original_name || contract.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                {fmtDate(contract.createdAt)}
                {contract.file_size ? ` · ${fmtSize(contract.file_size)}` : ''}
                {contract.employee ? ` · ${contract.employee.first_name} ${contract.employee.last_name}` : ''}
              </div>
            </div>
            {fileUrl && (
              <a href={fileUrl} target="_blank" rel="noreferrer"
                style={{
                  fontSize: 12, color: 'var(--text-color-calendar-week)',
                  background: 'var(--btn-user-btn)', border: '1px solid var(--border-input)',
                  borderRadius: 5, padding: '4px 10px', textDecoration: 'none',
                }}>
                ↓ {t('Download')}
              </a>
            )}
          </div>
          <div style={{ padding: 16 }}>
            {fileUrl ? (
              <iframe
                src={fileUrl}
                title="Contract document"
                style={{ width: '100%', height: 500, border: 'none', borderRadius: 4, display: 'block' }}
              />
            ) : (
              <div style={{
                height: 200, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8,
                color: 'var(--text-color-calendar-week)',
              }}>
                <div style={{ fontSize: 28 }}>📄</div>
                <div style={{ fontSize: 13 }}>{t('NoFileAttached')}</div>
                {canManage && (
                  <label style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    background: 'var(--btn-user-btn)', border: '1px solid var(--border-input)',
                    color: 'var(--text-color)',
                  }}>
                    {t('UploadFile')}
                    <input type="file" accept=".pdf,.docx,.doc" style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('file', file);
                        try { await axios.post(`document-contract/${contract.id}/file`, fd); }
                        catch { /* ignore */ }
                      }} />
                  </label>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: signature panel */}
        <div style={{
          background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
          borderRadius: 6, overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--bg-top)', borderBottom: '1px solid var(--border-input)',
            padding: '10px 14px',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>
              {t('ElectronicSignature')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
              {t('DrawSignatureHint')}
            </div>
          </div>

          {/* Steps */}
          <div style={{
            display: 'flex', alignItems: 'center', padding: '10px 14px',
            borderBottom: '1px solid var(--border-input)',
          }}>
            {(['Review', 'Sign', 'Submit'] as const).map((step, i) => (
              <React.Fragment key={step}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    border: `1.5px solid ${i === 0 ? STATUS_COLORS.signed : i === 1 && !signedState ? '#2563eb' : signedState ? STATUS_COLORS.signed : 'var(--border-input)'}`,
                    background: i === 0 ? STATUS_COLORS.signed : i === 1 && !signedState ? '#2563eb' : signedState && i === 2 ? STATUS_COLORS.signed : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: (i === 0 || (signedState && i === 2) || (i === 1 && !signedState)) ? '#fff' : 'var(--text-color-calendar-week)',
                    fontWeight: 700, flexShrink: 0,
                  }}>
                    {i === 0 || (signedState && i === 2) ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: i === 1 && !signedState ? '#2563eb' : 'var(--text-color-calendar-week)' }}>
                    {t(step === 'Review' ? 'StepReview' : step === 'Sign' ? 'StepSign' : 'StepSubmit')}
                  </span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 1, background: 'var(--border-input)', margin: '0 6px' }} />}
              </React.Fragment>
            ))}
          </div>

          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {signedState ? (
              /* Signed success state */
              <div style={{ textAlign: 'center', padding: '20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: `${STATUS_COLORS.signed}22`, border: `2px solid ${STATUS_COLORS.signed}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, color: STATUS_COLORS.signed,
                }}>✓</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)' }}>{t('ContractSigned')}</div>
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                  {contract.signedAt ? fmtDate(contract.signedAt) : fmtDate(new Date().toISOString())}
                </div>
                <button type="button" onClick={onBack}
                  style={{
                    marginTop: 8, padding: '6px 16px', borderRadius: 5, border: 'none',
                    background: 'var(--btn-user-btn)', color: 'var(--text-color)',
                    fontSize: 13, cursor: 'pointer',
                  }}>
                  {t('BackToContracts')}
                </button>
              </div>
            ) : contract.status === 'draft' ? (
              <div style={{ textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12, padding: '12px 0' }}>
                {t('ContractDraftNotice')}
              </div>
            ) : (
              /* Signing form */
              <>
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                  {t('SignAs')}:&nbsp;
                  <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>
                    {contract.employee?.first_name} {contract.employee?.last_name}
                  </span>
                </div>

                <SignatureCanvas
                  hasSignature={hasSignature}
                  onSign={(b64) => { setSigBase64(b64); setHasSignature(true); }}
                  onClear={() => { setSigBase64(''); setHasSignature(false); }}
                />

                <div style={{ borderTop: '1px solid var(--border-input)', paddingTop: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', lineHeight: 1.5, marginBottom: 10 }}>
                    {t('SignLegalNotice')}
                  </div>
                  <button
                    type="button"
                    onClick={handleSign}
                    disabled={!hasSignature || signing}
                    style={{
                      width: '100%', padding: 10, borderRadius: 6, border: 'none',
                      background: hasSignature ? '#2563eb' : 'var(--border-input)',
                      color: hasSignature ? '#fff' : 'var(--text-color-calendar-week)',
                      fontSize: 13, fontWeight: 600,
                      cursor: hasSignature ? 'pointer' : 'default',
                      opacity: signing ? .7 : 1,
                      transition: 'background .15s, color .15s',
                    }}>
                    {signing ? t('Saving') : t('SignAndSubmit')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main ContractsTab ───────────────────────────────────
const ContractsTab: React.FC<Props> = ({ access, baseUrl }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [selected, setSelected] = useState<Contract | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addEmployeeId, setAddEmployeeId] = useState('');
  const [addDeadline, setAddDeadline] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addFile, setAddFile] = useState<File | null>(null);
  const [employees, setEmployees] = useState<{ id: number; first_name: string; last_name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canManage = access?.document_add === true;

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('document-contract');
      setContracts(data);
    } catch { /* handled by global interceptor */ }
    finally { setLoading(false); }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get('user');
      if (Array.isArray(data)) setEmployees(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (auth) {
      fetchContracts();
      if (canManage) fetchEmployees();
    }
  }, [auth, fetchContracts, fetchEmployees, canManage]);

  const contractCols: ColDef<Contract>[] = [
    {
      key: 'status', label: t('Status'), render: () => null,
      filterType: 'select', filterExtract: (c) => c.status ?? '',
      filterOptions: [
        { value: 'draft', label: t('ContractDraft') as string },
        { value: 'pending', label: t('ContractPending') as string },
        { value: 'signed', label: t('ContractSigned') as string },
      ],
    },
    {
      key: 'employee', label: t('Employee'), render: () => null,
      filterType: 'select', filterExtract: (c) => c.employee ? String(c.employee.id) : '',
      filterOptions: employees.map(e => ({ value: String(e.id), label: `${e.first_name} ${e.last_name}` })),
    },
    {
      key: 'deadline', label: t('Deadline'), render: () => null,
      filterType: 'daterange', filterExtract: (c) => c.deadline ?? '',
    },
  ];

  const filtered = applySearchAndFilters(contracts, search, filters, contractCols,
    (c) => [c.title, c.employee ? `${c.employee.first_name} ${c.employee.last_name}` : ''].filter(Boolean).join(' '));

  const counts = {
    total:   contracts.length,
    signed:  contracts.filter(c => c.status === 'signed').length,
    pending: contracts.filter(c => c.status === 'pending').length,
    draft:   contracts.filter(c => c.status === 'draft').length,
  };

  const handleCreate = async () => {
    if (!addTitle.trim()) return;
    setSaving(true);
    try {
      const { data: created } = await axios.post('document-contract', {
        title: addTitle.trim(),
        description: addDesc.trim() || undefined,
        employeeId: addEmployeeId ? Number(addEmployeeId) : undefined,
        deadline: addDeadline || undefined,
      });
      if (addFile) {
        const fd = new FormData();
        fd.append('file', addFile);
        await axios.post(`document-contract/${created.id}/file`, fd);
      }
      setShowAdd(false);
      setAddTitle(''); setAddEmployeeId(''); setAddDeadline(''); setAddDesc(''); setAddFile(null);
      await fetchContracts();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('DeleteContractConfirm') as string)) return;
    try {
      await axios.delete(`document-contract/${id}`);
      setContracts(prev => prev.filter(c => c.id !== id));
    } catch { /* ignore */ }
  };

  if (selected) {
    return (
      <ContractViewer
        contract={selected}
        baseUrl={baseUrl}
        canManage={canManage}
        onBack={() => { setSelected(null); fetchContracts(); }}
        onSigned={(updated) => setSelected(prev => prev ? { ...prev, ...updated } : prev)}
      />
    );
  }

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
        {([
          { label: t('Total'), val: counts.total, color: 'var(--text-color)' },
          { label: t('ContractSigned'), val: counts.signed, color: STATUS_COLORS.signed },
          { label: t('ContractPending'), val: counts.pending, color: STATUS_COLORS.pending },
          { label: t('ContractDraft'), val: counts.draft, color: STATUS_COLORS.draft },
        ] as const).map(s => (
          <div key={s.label} style={{
            background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
            borderRadius: 6, padding: '10px 12px',
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.val}</div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <SearchFilter grow columns={contractCols} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} placeholder={t('SearchContracts') as string} />
        <div style={{ flex: 1 }} />
        {canManage && (
          <>
            <button
              type="button"
              onClick={() => setShowDrop(d => !d)}
              className="dt-btn-ghost"
              style={{ fontSize: 12 }}>
              ↑ {t('UploadContract')}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              style={{
                fontSize: 12, padding: '6px 14px', borderRadius: 5, border: 'none',
                background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600,
              }}>
              + {t('NewContract')}
            </button>
          </>
        )}
      </div>

      {/* Drop zone */}
      {showDrop && canManage && (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={async (e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (!file) return;
            const fd = new FormData();
            const { data: created } = await axios.post('document-contract', { title: file.name.replace(/\.[^.]+$/, '') });
            fd.append('file', file);
            await axios.post(`document-contract/${created.id}/file`, fd);
            setShowDrop(false);
            fetchContracts();
          }}
          style={{
            border: '2px dashed var(--border-input)', borderRadius: 6,
            background: 'var(--btn-user-form)', padding: '28px 20px',
            textAlign: 'center', cursor: 'pointer', marginBottom: 10,
          }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>📄</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{t('DropContractHere')}</div>
          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 3 }}>{t('DropContractSub')}</div>
          <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc" aria-label="Upload contract file" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData();
              const { data: created } = await axios.post('document-contract', { title: file.name.replace(/\.[^.]+$/, '') });
              fd.append('file', file);
              await axios.post(`document-contract/${created.id}/file`, fd);
              setShowDrop(false);
              fetchContracts();
            }} />
        </div>
      )}

      {/* Contracts table */}
      <div style={{ background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 6, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '3px 1fr 160px 110px 110px 90px 36px',
          gap: 0, background: 'var(--background-color)',
          borderBottom: '1px solid var(--border-input)',
          padding: '8px 0',
        }}>
          <div />
          {[t('ContractTitle'), t('Employee'), t('Created'), t('Deadline'), t('Status')].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-color-calendar-week)', padding: '0 14px' }}>{h}</div>
          ))}
          <div />
        </div>

        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>{t('Loading')}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{t('NoContracts')}</div>
          </div>
        ) : (
          filtered.map(c => (
            <div
              key={c.id}
              onClick={() => setSelected(c)}
              style={{
                display: 'grid', gridTemplateColumns: '3px 1fr 160px 110px 110px 90px 36px',
                alignItems: 'center', cursor: 'pointer',
                borderBottom: '1px solid var(--border-input)',
                transition: 'background .1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--active-row-color, rgba(37,99,235,.04))')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}>
              <div style={{ width: 3, height: '100%', background: STATUS_COLORS[c.status], borderRadius: '0 2px 2px 0' }} />
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)' }}>{c.title}</div>
                {c.description && <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>{c.description}</div>}
              </div>
              <div style={{ padding: '0 14px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                {c.employee ? `${c.employee.first_name} ${c.employee.last_name}` : '—'}
              </div>
              <div style={{ padding: '0 14px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{fmtDate(c.createdAt)}</div>
              <div style={{ padding: '0 14px', fontSize: 12, color: c.deadline && new Date(c.deadline) < new Date() && c.status !== 'signed' ? '#dc2626' : 'var(--text-color-calendar-week)' }}>
                {fmtDate(c.deadline)}
              </div>
              <div style={{ padding: '0 14px' }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: `${STATUS_COLORS[c.status]}22`,
                  color: STATUS_COLORS[c.status],
                  border: `1px solid ${STATUS_COLORS[c.status]}44`,
                }}>
                  {statusLabel(c.status)}
                </span>
              </div>
              <div style={{ padding: '0 8px' }} onClick={e => e.stopPropagation()}>
                {canManage && (
                  <button
                    type="button"
                    onClick={e => handleDelete(c.id, e)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-color-calendar-week)', fontSize: 14, padding: 4,
                      borderRadius: 4, lineHeight: 1,
                    }}
                    title={t('Delete') as string}>
                    ×
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add contract modal */}
      {showAdd && (
        <div
          data-portal-modal="true"
          style={{
            position: 'fixed', inset: 0,
            zIndex: 'var(--z-modal, 400)' as any,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)' }} />
          <div style={{
            position: 'relative', width: 460, background: 'var(--bg-form)',
            borderRadius: 8, border: '1px solid var(--border-input)',
            boxShadow: '0 8px 32px rgba(0,0,0,.2)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 18px', background: 'var(--bg-top)',
              borderBottom: '1px solid var(--border-input)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-color)' }}>{t('NewContract')}</span>
              <button type="button" onClick={() => setShowAdd(false)}
                style={{ background: 'none', border: 'none', fontSize: 18, color: 'var(--text-color-calendar-week)', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('ContractTitle')} *</label>
                <input className="timesheet-edit-input" style={{ width: '100%' }} value={addTitle} onChange={e => setAddTitle(e.target.value)} placeholder={t('ContractTitlePlaceholder') as string} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Employee')}</label>
                <select className="timesheet-edit-input" style={{ width: '100%' }} value={addEmployeeId} onChange={e => setAddEmployeeId(e.target.value)}>
                  <option value="">{t('Select')}</option>
                  {employees.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Deadline')}</label>
                <CalendarPicker mode="single" variant="field" output="string" value={addDeadline} onChange={(v) => setAddDeadline(v)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('ContractFile')}</label>
                <label style={{
                  display: 'block', padding: '8px 12px', borderRadius: 5, cursor: 'pointer',
                  background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
                  fontSize: 12, color: addFile ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                  textAlign: 'center',
                }}>
                  {addFile ? addFile.name : t('ChooseFile')}
                  <input type="file" accept=".pdf,.docx,.doc" aria-label="Choose contract file" style={{ display: 'none' }} onChange={e => setAddFile(e.target.files?.[0] ?? null)} />
                </label>
              </div>
            </div>
            <div style={{ padding: '10px 18px', background: 'var(--bg-top-fix)', borderTop: '1px solid var(--border-input)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setShowAdd(false)}
                style={{ background: 'var(--btn-user-btn)', color: 'var(--text-color)', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
                {t('Cancel')}
              </button>
              <button type="button" onClick={handleCreate} disabled={!addTitle.trim() || saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 13, cursor: 'pointer', opacity: !addTitle.trim() || saving ? .6 : 1 }}>
                {saving ? t('Saving') : t('Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsTab;
